import React, { useState } from 'react';
import { FormControlLabel, Switch, Typography } from '@mui/material';
import { ComposableMap, Geographies, Geography, Marker } from 'react-simple-maps';
import TravelMapShell from './TravelMapShell.jsx';
import { trackLabelsToggle } from '../utils/analytics';
import { PALETTE } from '../theme';
import { NATURE_DEFAULT_COLOR } from './mapDefaultColors';
import { LABEL_OUTLINE_COLOR, LABEL_OUTLINE_WIDTH, LABEL_OUTLINE_WIDTH_HOVER } from './mapGeographyStyles';

const US_GEO_URL = "https://cdn.jsdelivr.net/npm/us-atlas@3/states-10m.json";

// All 63 US National Parks
// Note: American Samoa and Virgin Islands use display coordinates (over Gulf of Mexico) since they're outside geoAlbersUsa projection range
const MAIN_MAP_PARKS = [
    { id: "american-samoa", name: "American Samoa", state: "American Samoa", coords: [-122, 31], isTerritory: true },
    { id: "virgin-islands", name: "Virgin Islands", state: "U.S. Virgin Islands", coords: [-122, 28.8], isTerritory: true },
    { id: "acadia", name: "Acadia", state: "Maine", coords: [-68.2733, 44.3386] },
    { id: "arches", name: "Arches", state: "Utah", coords: [-109.5863, 38.7331] },
    { id: "badlands", name: "Badlands", state: "South Dakota", coords: [-102.3397, 43.8554] },
    { id: "big-bend", name: "Big Bend", state: "Texas", coords: [-103.2502, 29.2500] },
    { id: "biscayne", name: "Biscayne", state: "Florida", coords: [-80.2100, 25.4824] },
    { id: "black-canyon", name: "Black Canyon of the Gunnison", state: "Colorado", coords: [-107.7416, 38.5754] },
    { id: "bryce-canyon", name: "Bryce Canyon", state: "Utah", coords: [-112.1871, 37.5930] },
    { id: "canyonlands", name: "Canyonlands", state: "Utah", coords: [-109.8783, 38.2000] },
    { id: "capitol-reef", name: "Capitol Reef", state: "Utah", coords: [-111.2615, 38.2000] },
    { id: "carlsbad", name: "Carlsbad Caverns", state: "New Mexico", coords: [-104.5567, 32.1479] },
    { id: "channel-islands", name: "Channel Islands", state: "California", coords: [-119.7217, 34.0069] },
    { id: "congaree", name: "Congaree", state: "South Carolina", coords: [-80.7821, 33.7948] },
    { id: "crater-lake", name: "Crater Lake", state: "Oregon", coords: [-122.1685, 42.8684] },
    { id: "cuyahoga", name: "Cuyahoga Valley", state: "Ohio", coords: [-81.5498, 41.2808] },
    { id: "death-valley", name: "Death Valley", state: "California/Nevada", coords: [-117.0794, 36.5054] },
    { id: "denali", name: "Denali", state: "Alaska", coords: [-150.4934, 63.1148] },
    { id: "dry-tortugas", name: "Dry Tortugas", state: "Florida", coords: [-82.8732, 24.6285] },
    { id: "everglades", name: "Everglades", state: "Florida", coords: [-80.8987, 25.2866] },
    { id: "gates-of-arctic", name: "Gates of the Arctic", state: "Alaska", coords: [-153.3000, 67.7863] },
    { id: "gateway-arch", name: "Gateway Arch", state: "Missouri", coords: [-90.1847, 38.6247] },
    { id: "glacier", name: "Glacier", state: "Montana", coords: [-113.7870, 48.7596] },
    { id: "glacier-bay", name: "Glacier Bay", state: "Alaska", coords: [-136.8407, 58.6658] },
    { id: "grand-canyon", name: "Grand Canyon", state: "Arizona", coords: [-112.1129, 36.1070] },
    { id: "grand-teton", name: "Grand Teton", state: "Wyoming", coords: [-110.6818, 43.7904] },
    { id: "great-basin", name: "Great Basin", state: "Nevada", coords: [-114.2631, 38.9830] },
    { id: "great-sand-dunes", name: "Great Sand Dunes", state: "Colorado", coords: [-105.5943, 37.7916] },
    { id: "great-smoky", name: "Great Smoky Mountains", state: "Tennessee/N. Carolina", coords: [-83.5070, 35.6532] },
    { id: "guadalupe", name: "Guadalupe Mountains", state: "Texas", coords: [-104.8606, 31.9231] },
    { id: "haleakala", name: "Haleakalā", state: "Hawaii", coords: [-156.1591, 20.7097] },
    { id: "hawaii-volcanoes", name: "Hawai'i Volcanoes", state: "Hawaii", coords: [-155.2885, 19.3800] },
    { id: "hot-springs", name: "Hot Springs", state: "Arkansas", coords: [-93.0552, 34.5217] },
    { id: "indiana-dunes", name: "Indiana Dunes", state: "Indiana", coords: [-87.0524, 41.6533] },
    { id: "isle-royale", name: "Isle Royale", state: "Michigan", coords: [-88.5500, 48.0000] },
    { id: "joshua-tree", name: "Joshua Tree", state: "California", coords: [-115.9010, 33.8734] },
    { id: "katmai", name: "Katmai", state: "Alaska", coords: [-155.0631, 58.5970] },
    { id: "kenai-fjords", name: "Kenai Fjords", state: "Alaska", coords: [-149.6500, 59.9223] },
    { id: "kings-canyon", name: "Kings Canyon", state: "California", coords: [-118.5551, 36.8879] },
    { id: "kobuk-valley", name: "Kobuk Valley", state: "Alaska", coords: [-159.2836, 67.3556] },
    { id: "lake-clark", name: "Lake Clark", state: "Alaska", coords: [-153.4167, 60.4167] },
    { id: "lassen-volcanic", name: "Lassen Volcanic", state: "California", coords: [-121.4076, 40.4977] },
    { id: "mammoth-cave", name: "Mammoth Cave", state: "Kentucky", coords: [-86.1000, 37.1870] },
    { id: "mesa-verde", name: "Mesa Verde", state: "Colorado", coords: [-108.4618, 37.1853] },
    { id: "mount-rainier", name: "Mount Rainier", state: "Washington", coords: [-121.7269, 46.8800] },
    { id: "new-river-gorge", name: "New River Gorge", state: "West Virginia", coords: [-81.0812, 37.8615] },
    { id: "north-cascades", name: "North Cascades", state: "Washington", coords: [-121.2069, 48.7718] },
    { id: "olympic", name: "Olympic", state: "Washington", coords: [-123.4979, 47.8021] },
    { id: "petrified-forest", name: "Petrified Forest", state: "Arizona", coords: [-109.7890, 35.0657] },
    { id: "pinnacles", name: "Pinnacles", state: "California", coords: [-121.1825, 36.4906] },
    { id: "redwood", name: "Redwood", state: "California", coords: [-124.0046, 41.2132] },
    { id: "rocky-mountain", name: "Rocky Mountain", state: "Colorado", coords: [-105.6836, 40.3428] },
    { id: "saguaro", name: "Saguaro", state: "Arizona", coords: [-110.5000, 32.1800] },
    { id: "sequoia", name: "Sequoia", state: "California", coords: [-118.5657, 36.4864] },
    { id: "shenandoah", name: "Shenandoah", state: "Virginia", coords: [-78.4679, 38.4755] },
    { id: "theodore-roosevelt", name: "Theodore Roosevelt", state: "North Dakota", coords: [-103.4300, 46.9790] },
    { id: "voyageurs", name: "Voyageurs", state: "Minnesota", coords: [-92.8833, 48.5000] },
    { id: "white-sands", name: "White Sands", state: "New Mexico", coords: [-106.1714, 32.7872] },
    { id: "wind-cave", name: "Wind Cave", state: "South Dakota", coords: [-103.4772, 43.5724] },
    { id: "wrangell-st-elias", name: "Wrangell-St. Elias", state: "Alaska", coords: [-142.9857, 61.0000] },
    { id: "yellowstone", name: "Yellowstone", state: "Wyoming/Montana/Idaho", coords: [-110.5885, 44.4280] },
    { id: "yosemite", name: "Yosemite", state: "California", coords: [-119.5383, 37.8651] },
    { id: "zion", name: "Zion", state: "Utah", coords: [-113.0263, 37.2982] }
].sort((a, b) => a.name.localeCompare(b.name));

// Combined list of all 63 parks for checklist (already sorted in MAIN_MAP_PARKS)
const ALL_PARKS = MAIN_MAP_PARKS;
const ITEMS = ALL_PARKS.map(p => ({ id: p.id, name: p.name, subtitle: p.state }));

// Custom label offsets ONLY for parks that overlap with default positioning
// Default is: { x: 0, y: -9, anchor: "middle" }
const LABEL_OFFSETS = {
    // Alaska - dense cluster
    "denali": { x: 0, y: -7, anchor: "middle" },
    "kobuk-valley": { x: -8, y: -5, anchor: "end" },
    "gates-of-arctic": { x: 8, y: -5, anchor: "start" },
    "wrangell-st-elias": { x: 8, y: -5, anchor: "start" },
    "lake-clark": { x: -6, y: -3, anchor: "end" },
    "katmai": { x: 0, y: 12, anchor: "middle" },
    "kenai-fjords": { x: -8, y: 9, anchor: "start" },
    "glacier-bay": { x: 7, y: 2, anchor: "start" },

    // Washington - overlapping
    "olympic": { x: -8, y: -5, anchor: "end" },
    "mount-rainier": { x: 0, y: 12, anchor: "middle" },
    "north-cascades": { x: 8, y: -5, anchor: "start" },

    // Wyoming - Grand Teton/Yellowstone overlap
    "grand-teton": { x: 0, y: 12, anchor: "middle" },

    // Dakotas - Badlands/Wind Cave overlap
    "badlands": { x: 8, y: -5, anchor: "start" },
    "wind-cave": { x: 0, y: 12, anchor: "middle" },

    // Utah/Colorado - dense cluster
    "death-valley": { x: 0, y: 12, anchor: "middle" },
    "capitol-reef": { x: -5, y: -5, anchor: "end" },
    "bryce-canyon": { x: -3, y: -5, anchor: "end" },
    "zion": { x: -8, y: 2, anchor: "end" },
    "arches": { x: 8, y: -5, anchor: "start" },
    "canyonlands": { x: 5, y: 8, anchor: "start" },
    "black-canyon": { x: 7, y: 2, anchor: "start" },
    "mesa-verde": { x: -8, y: 0, anchor: "end" },
    "great-sand-dunes": { x: 8, y: -5, anchor: "start" },
    "grand-canyon": { x: -8, y: 0, anchor: "end" },

    // California - overlapping
    "yosemite": { x: -8, y: -5, anchor: "end" },
    "sequoia": { x: -8, y: 5, anchor: "end" },
    "kings-canyon": { x: 0, y: -7, anchor: "middle" },
    "pinnacles": { x: -8, y: -5, anchor: "end" },

    // Florida - Biscayne/Everglades overlap
    "biscayne": { x: 6, y: -3, anchor: "start" },
    "everglades": { x: -6, y: -3, anchor: "end" },
    "dry-tortugas": { x: -28, y: 1, anchor: "middle" },

    // New Mexico/Texas - Carlsbad/Guadalupe overlap
    "carlsbad": { x: 8, y: -5, anchor: "start" },
    "guadalupe": { x: 0, y: 12, anchor: "middle" },
    "white-sands": { x: 8, y: -5, anchor: "start" },

    // Territory parks (displayed over Gulf of Mexico)
    "american-samoa": { x: 7, y: 3, anchor: "start" },
    "virgin-islands": { x: 7, y: 3, anchor: "start" }
};

const NationalParksMapSection = () => {
    const [showLabels, setShowLabels] = useState(true);
    const [hoveredPark, setHoveredPark] = useState(null);

    const renderMap = ({ visited, hexColor, toggle }) => (
        <ComposableMap
            projection="geoAlbersUsa"
            projectionConfig={{
                scale: 1000
            }}
            width={900}
            height={600}
            style={{ width: '100%', height: 'auto' }}
        >
            {/* US States Background */}
            <Geographies geography={US_GEO_URL}>
                {({ geographies }) =>
                    geographies.map((geo) => (
                        <Geography
                            key={geo.rsmKey}
                            geography={geo}
                            style={{
                                default: {
                                    fill: '#f0f0f0',
                                    stroke: '#cccccc',
                                    strokeWidth: 2.5,
                                    outline: 'none'
                                },
                                hover: {
                                    fill: '#f0f0f0',
                                    stroke: '#cccccc',
                                    strokeWidth: 2.5,
                                    outline: 'none'
                                }
                            }}
                        />
                    ))
                }
            </Geographies>

            {/* Park Markers - Main Map */}
            {MAIN_MAP_PARKS.map((park) => {
                const isVisited = visited.includes(park.id);
                const isHovered = hoveredPark === park.id;

                return (
                    <Marker
                        key={park.id}
                        coordinates={park.coords}
                        onClick={() => toggle(park.id)}
                        onMouseEnter={() => setHoveredPark(park.id)}
                        onMouseLeave={() => setHoveredPark(null)}
                        style={{ cursor: 'pointer' }}
                    >
                        <circle
                            r={isHovered ? 8 : 6}
                            fill={isVisited ? hexColor : PALETTE.textVeryFaint}
                            stroke={isVisited ? '#ffffff' : '#95a5a6'}
                            strokeWidth={isHovered ? 2.5 : 1.5}
                            style={{
                                transition: 'all 0.2s ease'
                            }}
                        />
                        {/* Show label on hover OR when visited (if labels enabled) */}
                        {(isHovered || (showLabels && isVisited)) && (() => {
                            const offset = LABEL_OFFSETS[park.id] || { x: 0, y: -9, anchor: "middle" };
                            return (
                                <text
                                    textAnchor={isHovered ? "middle" : offset.anchor}
                                    x={isHovered ? 0 : offset.x}
                                    y={isHovered ? -10 : offset.y}
                                    style={{
                                        fontFamily: 'system-ui',
                                        fontSize: isHovered ? 10 : 6,
                                        fontWeight: 'bold',
                                        fill: isHovered ? PALETTE.text : hexColor,
                                        pointerEvents: 'none',
                                        paintOrder: 'stroke',
                                        stroke: LABEL_OUTLINE_COLOR,
                                        strokeWidth: isHovered ? LABEL_OUTLINE_WIDTH_HOVER : LABEL_OUTLINE_WIDTH,
                                        strokeLinejoin: 'round'
                                    }}
                                >
                                    {park.name}
                                </text>
                            );
                        })()}
                    </Marker>
                );
            })}

            {/* Region Labels */}
            <Marker coordinates={[-173, 62]}>
                <text
                    textAnchor="middle"
                    y={-10}
                    x={-15}
                    style={{
                        fontFamily: 'system-ui',
                        fontSize: 8,
                        fill: PALETTE.textMuted,
                        fontWeight: 'bold'
                    }}
                >
                    Alaska
                </text>
            </Marker>
            <Marker coordinates={[-155, 20]}>
                <text
                    textAnchor="middle"
                    y={-5}
                    x={-50}
                    style={{
                        fontFamily: 'system-ui',
                        fontSize: 8,
                        fill: PALETTE.textMuted,
                        fontWeight: 'bold'
                    }}
                >
                    Hawaii
                </text>
            </Marker>
            <Marker coordinates={[-122, 30.5]}>
                <text
                    textAnchor="middle"
                    y={-18}
                    style={{
                        fontFamily: 'system-ui',
                        fontSize: 7,
                        fill: PALETTE.textMuted,
                        fontWeight: 'bold'
                    }}
                >
                    American Samoa
                </text>
            </Marker>
            <Marker coordinates={[-122, 28.5]}>
                <text
                    textAnchor="middle"
                    y={-15}
                    style={{
                        fontFamily: 'system-ui',
                        fontSize: 7,
                        fill: PALETTE.textMuted,
                        fontWeight: 'bold'
                    }}
                >
                    U.S. Virgin Islands
                </text>
            </Marker>
        </ComposableMap>
    );

    return (
        <TravelMapShell
            mapType="national_parks"
            analyticsLocationType="national_park"
            trackingLabel="National Parks Map"
            storageKey="parks_map"
            title="🏞️ US National Parks Travel Map"
            subtitle="Track your visits to all 63 national parks!"
            items={ITEMS}
            noun="Parks"
            unitLabel="parks"
            captionUnit="US national parks"
            share={{
                region: 'US National Parks',
                fileName: 'visited-national-parks-map.png',
                trackId: 'national_parks'
            }}
            defaultColor={NATURE_DEFAULT_COLOR}
            extraOptions={
                <FormControlLabel
                    control={
                        <Switch
                            checked={showLabels}
                            onChange={(e) => { setShowLabels(e.target.checked); trackLabelsToggle('national_parks', e.target.checked); }}
                            size="small"
                        />
                    }
                    label={<Typography variant="body2" sx={{ fontSize: '13px' }}>Show Park Names</Typography>}
                />
            }
            showLegend
            renderMap={renderMap}
        />
    );
};

export default NationalParksMapSection;
